/* Shared plumbing for the Docker admin tabs: the per-tab staleness cache that
   survives tab switches, and the toolbar / bulk-bar chrome each tab repeats. */
import {
  useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode,
} from 'react';
import { api } from '../../../api/client';

export const DOCKER_BASE = '/api/admin/docker';
export const TABS = ['containers', 'images', 'networks', 'volumes', 'stacks'] as const;
export type TabId = (typeof TABS)[number];

const STALE_MS = 30000;
export const POLL_MS = 10000;

export function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** On the apple backend a container id is a user-chosen name, so every segment escapes. */
export const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

export interface CacheEntry<T> { data: T | null; fetchedAt: number }

export interface DockerCache {
  read<T>(key: TabId): CacheEntry<T>;
  write(key: TabId, data: unknown): void;
}

const MISSING = { data: null, fetchedAt: 0 };

/**
 * Lives on the page, not the tab, so switching away and back inside the
 * staleness window does not refetch. Reads go through a ref; writes bump a
 * counter so the page re-renders with the new data.
 */
export function useDockerCache(): DockerCache {
  const store = useRef<Partial<Record<TabId, CacheEntry<unknown>>>>({});
  const [, bump] = useReducer((n: number) => n + 1, 0);

  const read = useCallback(<T,>(key: TabId): CacheEntry<T> =>
    (store.current[key] ?? MISSING) as unknown as CacheEntry<T>, []);

  const write = useCallback((key: TabId, data: unknown) => {
    store.current[key] = { data, fetchedAt: Date.now() };
    bump();
  }, []);

  return useMemo(() => ({ read, write }), [read, write]);
}

export interface TabResource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: (force?: boolean) => Promise<void>;
}

export function useTabResource<T, R>(opts: {
  cache: DockerCache;
  tab: TabId;
  path: string;
  extract: (raw: R) => T;
  /** Bumped by the page (e.g. after a system prune) to force every tab stale. */
  refreshToken: number;
}): TabResource<T> {
  const { cache, tab, path, extract, refreshToken } = opts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const extractRef = useRef(extract);
  extractRef.current = extract;

  const reload = useCallback(async (force = false) => {
    const entry = cache.read<T>(tab);
    if (!force && entry.data !== null && Date.now() - entry.fetchedAt < STALE_MS) return;
    if (entry.data === null) setLoading(true);
    try {
      const raw = await api.get<R>(path);
      cache.write(tab, extractRef.current(raw));
      setError(null);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  }, [cache, tab, path]);

  const mountToken = useRef(refreshToken);
  useEffect(() => { void reload(refreshToken !== mountToken.current); }, [reload, refreshToken]);

  return { data: cache.read<T>(tab).data, loading, error, reload };
}

// ---------------------------------------------------------------------------
// Chrome
// ---------------------------------------------------------------------------

export function TabToolbar({ onRefresh, flash, children }: {
  onRefresh: () => void;
  flash?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
      <button type="button" className="btn-ghost btn-sm" onClick={onRefresh}>Refresh</button>
      {children}
      <span style={{ marginLeft: 'auto', fontSize: '0.8rem' }}>{flash}</span>
    </div>
  );
}

export function BulkBar({ count, children }: { count: number; children: ReactNode }) {
  if (count === 0) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
      marginBottom: '0.75rem', padding: '0.5rem 0.75rem',
      background: 'var(--ha-bg-elevated)', borderRadius: '0.375rem',
    }}>
      <span className="muted" style={{ fontSize: '0.8rem' }}>{count} selected</span>
      {children}
    </div>
  );
}

export function DataTable({ headers, children }: { headers: ReactNode[]; children: ReactNode }) {
  return (
    <div className="scroll-x">
      <table className="data">
        <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function FormGrid({ min = 220, children }: { min?: number; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`, gap: '0.75rem' }}>
      {children}
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ fontSize: '0.8rem', color: 'var(--ha-text-muted)' }}>
      {label}
      {children}
    </label>
  );
}

export function CheckField({ label, checked, onChange }: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label style={{
      fontSize: '0.8rem', color: 'var(--ha-text-muted)', display: 'flex',
      alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem',
    }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );
}

export function FormActions({ children }: { children: ReactNode }) {
  return (
    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
      {children}
    </div>
  );
}

/** Truncating monospace cell — long images, mountpoints and paths. */
export function truncStyle(maxWidth: string): React.CSSProperties {
  return { maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
}

// ---------------------------------------------------------------------------
// Selection + bulk
// ---------------------------------------------------------------------------

export function useToggleSet(): [ReadonlySet<string>, (key: string) => void] {
  const [set, setSet] = useState<ReadonlySet<string>>(() => new Set<string>());
  const toggle = useCallback((key: string) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);
  return [set, toggle];
}

export function useSelection(selectableIds: string[]) {
  const [raw, setRaw] = useState<string[]>([]);
  const available = useMemo(() => new Set(selectableIds), [selectableIds]);
  const ids = useMemo(() => raw.filter((id) => available.has(id)), [raw, available]);

  const toggle = useCallback((id: string) => {
    setRaw((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const selectAll = useCallback((checked: boolean) => {
    setRaw(checked ? Array.from(available) : []);
  }, [available]);
  const clear = useCallback(() => setRaw([]), []);

  const allChecked = selectableIds.length > 0 && ids.length === selectableIds.length;
  return { ids, toggle, selectAll, clear, allChecked, has: (id: string) => ids.includes(id) };
}

/** Sequential so a backend that serialises anyway is not hammered; collects per-item errors. */
export async function runBulk(ids: string[], run: (id: string) => Promise<unknown>): Promise<string[]> {
  const errors: string[] = [];
  for (const id of ids) {
    try {
      await run(id);
    } catch (err) {
      errors.push(`${id.substring(0, 12)}: ${errMsg(err)}`);
    }
  }
  return errors;
}

export function makeBulkHandler(
  ids: string[],
  run: (id: string) => Promise<unknown>,
  show: (text: string, tone?: 'ok' | 'error' | 'warn') => void,
  after: () => Promise<void>,
) {
  return async () => {
    if (ids.length === 0) return;
    const errors = await runBulk(ids, run);
    if (errors.length) show(`${errors.length} failed — ${errors[0]}`, 'error');
    else show(`${ids.length} done`, 'ok');
    await after();
  };
}
